import Button from '@/Components/Button/Button';
import { Dialog, DialogButtons, DialogContent, DialogTitle } from '@/Components/Dialog/Dialog';
import { type ConfirmedNomenclature } from '@/Models/ConfirmedNomenclature';
import {
    type EFactRecord,
    type RecordType10,
    type RecordType20,
    type RecordType21,
    type RecordType50,
    type RecordType52,
    type RecordType80,
    type RecordType90,
    type RecordType95,
} from '@/Pages/EFactTool/EFactRecord';
import moment from 'moment';
import { type ChangeEvent, type MouseEventHandler, useState } from 'react';
import * as API from '../../API';
import Styles from './EFactTool.module.scss';

interface Header {
    naamBericht: string;
    versieNummerFormaatBericht: string;
    referentieBerichtZiekenhuis: string;
    referentieBerichtVI: string;
    jaarEnMaandVanFacturatie: string;
    nummerVanDeVerzendingen: string;
}

function EFactTool(): React.ReactElement {
    const [inputData, setInputData] = useState<EFactRecord[]>([]);
    const [header, setHeader] = useState<Header>();
    const [summary, setSummary] = useState<RecordType95>();
    const [invoices, setInvoices] = useState<EFactRecord[][]>([]);
    const [openResult, setOpenResult] = useState(false);
    const [diffs, setDiffs] = useState<ConfirmedNomenclature[]>([]);
    const [serverError, setServerError] = useState<string | null>();

    const get = (line: string, sPos: number, ePos: number, isHeader = false): string => {
        if (!isHeader) {
            return line.slice(sPos - 1, ePos);
        }
        return line.slice(sPos - 124, ePos + 1 - 124);
    };

    const readHeader = (line: string): void => {
        const obj: Header = {
            // Record 200
            naamBericht: get(line, 124, 129, true),
            versieNummerFormaatBericht: get(line, 132, 133, true),
            referentieBerichtZiekenhuis: get(line, 144, 157, true),
            referentieBerichtVI: get(line, 160, 173, true),

            // Record 300
            jaarEnMaandVanFacturatie: get(line, 191, 196, true),
            nummerVanDeVerzendingen: get(line, 199, 201, true), // transmission number
        };
        setHeader(obj);
    };

    const inputFileChanged = (e: ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files == null) {
            return console.error('No files found in input element to be parsed');
        }

        const file = e.target.files[0];
        const obj: EFactRecord[] = [];
        const reader = new FileReader();
        reader.onload = (evt) => {
            if (evt.target == null) {
                return console.error('Could read file');
            }

            // Split file into multiple lines per recordtype
            const totalStr = evt.target.result as string;
            if (totalStr == null) {
                return console.error('read text is null');
            }

            const lines = [totalStr.slice(0, 227)];
            for (let t = 227; t < totalStr.length; t += 350) {
                lines.push(totalStr.slice(t, t + 350));
            }

            // Parse separated lines
            lines.forEach((line, i) => {
                if (i === 0) {
                    return readHeader(line);
                }
                const recordType = Number(get(line, 1, 2));
                const record: EFactRecord = { type: recordType, volgnummer: get(line, 3, 8) };
                switch (recordType) {
                    case 10: { // Aanvang zending
                        const record10 = record as RecordType10;
                        record10.volgnummer = get(line, 3, 8);
                        record10.aantalFinancieleRekeningnummers = get(line, 9, 9);
                        record10.versieBestand = get(line, 10, 16);
                        record10.financieelRekeningnummerA = get(line, 17, 28);
                        record10.zendingsNummer = get(line, 33, 35);
                        record10.financieelRekeningnummerB = get(line, 36, 47);
                        record10.nummerDerdebetalende = get(line, 56, 67); // RIZIV from General Settings
                        record10.beroepsCode = get(line, 85, 87);
                        record10.gefactureerdJaar = get(line, 108, 112);
                        record10.gefactureerdeMaand = get(line, 113, 114);
                        record10.creationDate = get(line, 120, 127);
                        record10.KBO = get(line, 128, 137);
                        record10.bicFinancieleRekeningA = get(line, 167, 177);
                        record10.ibanFinancieleRekeningA = get(line, 179, 212);
                        record10.bicFinancieleRekeningB = get(line, 219, 229);
                        record10.ibanFinancieleRekeningB = get(line, 271, 304);
                        record10.controleCijferRecord = get(line, 349, 350);
                        obj.push(record10);
                        break;
                    }
                    case 20: { // Aanvang factuur
                        const temp = record as RecordType20;
                        temp.uurVanOpname = get(line, 10, 16);
                        temp.datumVanOpname = get(line, 17, 24);
                        temp.datumVanOntslag = get(line, 25, 32);
                        temp.nummerZiekenfondsVanAansluiting = get(line, 33, 35);
                        temp.identificatieRechthebbende = get(line, 36, 48);
                        temp.geslachtRechthebbende = get(line, 49, 49);
                        temp.typeFactuur = get(line, 50, 50);
                        temp.typeFacturering = get(line, 51, 51);
                        temp.nummerFacturerendeInstelling = get(line, 56, 67);
                        temp.instellingVanVerblijf = get(line, 68, 79);
                        temp.redenBehandeling = get(line, 81, 84);
                        temp.nummerZiekenfondsVanBestemming = get(line, 85, 87);
                        temp.nummerOpname = get(line, 88, 99);
                        temp.uurVanOntslag = get(line, 108, 112);
                        temp.nummerIndividueleFactuur = get(line, 115, 126);
                        temp.toepassingSocialeFranchise = get(line, 127, 127);
                        temp.CG1CG2 = get(line, 128, 137);
                        temp.referentieInstelling = get(line, 138, 162);
                        temp.datumVanFacturering = get(line, 262, 269);
                        temp.controleCijferRecord = get(line, 349, 350);
                        obj.push(temp);
                        break;
                    }
                    case 21: { // Lezing gegevens ziekenhuizen
                        const temp = record as RecordType21;
                        temp.uurVanLezingIdentiteitsdocument = get(line, 9, 12);
                        temp.datumVanLezingIdentiteitsdocument = get(line, 13, 20);
                        temp.redenGebruikVignet = get(line, 23, 23);
                        temp.serienummerVanDeDrager = get(line, 25, 39);
                        temp.controleCijferRecord = get(line, 349, 350);
                        obj.push(temp);
                        break;
                    }
                    case 30: break; // Verpleegdagen
                    case 40: break; // Farmaceutische producten
                    case 50: { // Verstrekkingen of leveringen
                        const temp = record as RecordType50;
                        temp.volgnummer = get(line, 3, 8);
                        temp.normVerstrekking = get(line, 9, 9);
                        temp.nomenclatuurCode = get(line, 10, 16);
                        temp.datumEersteVerrichting = get(line, 17, 24);
                        temp.datumLaatsteVerrichting = get(line, 25, 32);
                        temp.nummerZiekenfondsVanAansluiting = get(line, 33, 35);
                        temp.behandeldLid = get(line, 177, 177);
                        temp.identificatieRechthebbende = get(line, 36, 48);
                        temp.plaatsVanVerstrekking = get(line, 56, 67);
                        temp.identificatieVerstrekker = get(line, 68, 79);
                        temp.normVerstrekker = get(line, 80, 80);
                        temp.betrekkelijkeVerstrekking = get(line, 81, 87);
                        temp.tekenEnBedragVerzekeringsTegemoetkoming = get(line, 88, 99);
                        temp.identificatieVoorschrijver = get(line, 115, 126);
                        temp.tekenEnAandeelPatient = get(line, 128, 137);
                        temp.tekenEnBedragSupplement = get(line, 165, 174);
                        temp.basiswaardeVerstrekking = get(line, 262, 269);
                        obj.push(temp);
                        break;
                    }
                    case 52: { // Vervolg record 50
                        const temp = record as RecordType52;
                        temp.volgnummer = get(line, 3, 8);
                        temp.nomenclatuurCode = get(line, 10, 16);
                        temp.INSZ = get(line, 36, 48);
                        temp.RIZIVNummer = get(line, 68, 79);
                        temp.serienummerDrager = get(line, 80, 94);
                        temp.nummerBewijsstuk = get(line, 95, 119);
                        temp.uniekNummerToestel = get(line, 120, 131);
                        temp.controleCijfer = get(line, 349, 350);
                        obj.push(temp);
                        break;
                    }
                    case 80: { // Einde factuur
                        const temp = record as RecordType80;
                        temp.nummerZiekenfondsVanAansluiting = get(line, 33, 35); // 3 = ambulante, 1 = gehospitaliseerde patient
                        temp.typeFactuur = get(line, 50, 50); // 3 = ambulante, 1 = gehospitaliseerde patient
                        temp.nummerFacturerendeInstelling = get(line, 56, 67); // RIZIV from General Settings
                        temp.tekenEnTotaalBedragRekeningNrB = get(line, 68, 79);
                        temp.redenBehandeling = get(line, 81, 84);
                        temp.tekenEnTotaalBedragRekeningNrA = get(line, 88, 99);
                        temp.tekenEnAandeelPatient = get(line, 128, 137);
                        temp.tekenEnBedragSupplement = get(line, 165, 174);
                        obj.push(temp);
                        break;
                    }
                    case 90: { // Einde zending
                        const temp = record as RecordType90;
                        temp.tekenEnTotaalBedragRekeningNrB = get(line, 68, 79);
                        temp.tekenEnTotaalBedragRekeningNrA = get(line, 88, 99);
                        obj.push(temp);
                        break;
                    }
                    case 95: { // Summary (There can be multiple, only "saving" the last one as that should be enough). For example: IO 100 might have 95-records for 110, 120, 130,...
                        const temp = record as RecordType95;
                        temp.nummerMutualiteit = get(line, 5, 7);
                        obj.push(temp);
                        break;
                    }
                }
            });

            // All data
            setInputData(obj);

            // Invoices for rendering
            const inv: EFactRecord[][] = [];
            let c = -1;
            obj.forEach((x: EFactRecord) => {
                if (x.type === 20) {
                    c++;
                }
                if (!inv[c]) {
                    inv[c] = [] as EFactRecord[];
                }
                inv[c].push(x);
            });
            setInvoices(inv);
            setSummary(obj[obj.length - 1] as RecordType95);
        };
        reader.readAsText(file);
    };

    const hiddenStates: Function[] = [];
    let hidden = true;
    const addInvoiceState = (s: Function): void => {
        hiddenStates.push(s);
    };

    const toggleAll = (): void => {
        hidden = !hidden;
        hiddenStates.forEach((s) => s(hidden));
    };

    const exportJSON = (): void => {
        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(JSON.stringify(invoices)));
        element.setAttribute('download', 'exportJSON.json');
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const onClickCompareDBHandler = (): void => {
        if (header == null) {
            return console.error('Cannot reliably read Filename value from given file (no Header data read)');
        }
        if (summary == null) {
            return console.error('Cannot reliably read IO value from given file (no RecordType 95)');
        }

        const record10 = inputData.find((x) => x.type === 10) as RecordType10;
        if (record10 == null) {
            return console.error('No RecordType 10 data found in inputData. Issue with file?');
        }

        const nummerMutualiteit = Number.parseInt(summary.nummerMutualiteit);
        const fileName = header.nummerVanDeVerzendingen;
        const creationDate = moment(record10.creationDate).toDate();

        getLinkedNomenclaturesFor(nummerMutualiteit, fileName, creationDate)
            .then((linkedConfirmedNomenclatures) => findLinkedConfirmedNomenclaturesMissingInFile(invoices, linkedConfirmedNomenclatures))
            .then((missingNomenclatures: ConfirmedNomenclature[]) => {
                if (missingNomenclatures.length === 0) {
                    return setServerError('The EFact batch exists for this TFac file but could not find any Confirmed Nomenclatures');
                }
                else {
                    setDiffs(missingNomenclatures);
                    setOpenResult(true);
                }
            })
            .catch((err) => {
                setServerError(err);
            });
    };

    const findLinkedConfirmedNomenclaturesMissingInFile = (eFactInvoiceInFile: EFactRecord[][], linkedConfirmedNomenclatures: ConfirmedNomenclature[]): ConfirmedNomenclature[] => {
        if (linkedConfirmedNomenclatures.length === 0) {
            return [];
        }

        // Question: There are MORE distinct Invoice_UID entries than EFactInvoiceNumber's in some cases. How can that be?
        console.log('Searching TFac invoices vs Confirmed Invoices', eFactInvoiceInFile.length, new Set(linkedConfirmedNomenclatures.map((x) => x.EFactInvoiceNumber)));

        const nomenclaturesWithEFactInvoice = linkedConfirmedNomenclatures.map((nomenclature) => {
            const efactInvoice = eFactInvoiceInFile.find((x) => (x[0] as RecordType20).nummerIndividueleFactuur !== nomenclature.EFactInvoiceNumber);
            return { nomenclature, efactInvoice };
        });

        const nomenclaturesMissingInBatch = nomenclaturesWithEFactInvoice
            .filter((nomenclatureWithInvoice) => {
                const nomenclature = nomenclatureWithInvoice.nomenclature;
                const efactInvoice = nomenclatureWithInvoice.efactInvoice;
                if (!efactInvoice) { // No matching efact invoice found
                    return true;
                }

                const record50s = efactInvoice.filter((record) => record.type === 50) as RecordType50[];
                const hasMatching50Record = record50s.some((record50) => record50.nomenclatuurCode.endsWith(nomenclature.NomenclatureNumber));
                return !hasMatching50Record;
            })
            .map((nomenclatureWithInvoice) => nomenclatureWithInvoice.nomenclature);

        return nomenclaturesMissingInBatch;
    };

    const getLinkedNomenclaturesFor = async (nummerMutualiteit: number, nummerVanDeVerzendingen: string, createdAt: Date): Promise<ConfirmedNomenclature[]> => {
        // Check IOHelper.cs in ROW1 for these exceptions
        // 306: Solidaris Brabant
        // 675: Zeevarenden
        const io = [306, 675].includes(nummerMutualiteit)
            ? nummerMutualiteit
            : nummerMutualiteit - (nummerMutualiteit % 100);

        return await API.eFact.getNomenclaturesForBatchByIOAndFilename(`${io}`, nummerVanDeVerzendingen, createdAt);
    };

    return (
        <div className={Styles.efactTool}>
            { serverError != null && <ServerError err={serverError} onClose={(_) => setServerError(null)} /> }
            <CompareResultDialog open={openResult} onClose={(_) => setOpenResult(false)} diffs={diffs} totalAmount={inputData.filter((x) => x.type === 50).length} />
            <div style={{ display: 'flex' }}>
                <div>
                    <h3>Input File</h3>
                    <p>
                        Input the batch file from the server directly.
                    </p>
                    <div>
                        <span>Batch file: </span>
                        <input type="file" id="input" onChange={(e) => inputFileChanged(e)} />
                    </div>
                </div>
            </div>

            <br />
            <br />

            { inputData[0]
                ? (
                        <div>
                            <p>
                                <b>Total Invoices:</b>
                                {' '}
                                {invoices.length}
                            </p>
                            <div>
                                <h3>Header</h3>
                                <table>
                                    <tbody>
                                        {header && Object.keys(header).map((x: string, i: number) => (
                                            <tr key={'header-' + i}>
                                                <td>{x}</td>
                                                <td>{header[x as keyof Header]}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div>
                                <h3>Footer</h3>
                                <table>
                                    <tbody>
                                        {summary && Object.keys(summary).map((x: string, i: number) => (
                                            <tr key={'header-' + i}>
                                                <td>{x}</td>
                                                <td>{summary[x as keyof RecordType95]}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ display: 'flex' }}>
                                <div style={{ marginRight: '10px' }}>
                                    <h3>Record 10</h3>
                                    <table>
                                        <tbody>
                                            {Object.keys(inputData[0]).map((x: string, i: number) => (
                                                <tr key={'10-' + i}>
                                                    <td>{x}</td>
                                                    <td>{inputData[0][x as keyof EFactRecord]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div>
                                    <h3>Record 90</h3>
                                    <table>
                                        <tbody>
                                            {Object.keys(inputData.find((x) => x.type === 90)!).map((x, i) => (
                                                <tr key={'10-' + i}>
                                                    <td>{x}</td>
                                                    <td>{inputData.find((x) => x.type === 90)![x as keyof EFactRecord]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <h3>Invoices</h3>
                            <div className={Styles.actionButtons}>
                                <Button mediumGreen onClick={toggleAll}>Toggle All</Button>
                                <Button mediumGreen onClick={exportJSON}>Export JSON</Button>
                                <Button mediumGreen onClick={onClickCompareDBHandler}>Compare sent nomens with DB</Button>
                            </div>
                            {invoices.map((x, i) => <Invoice key={'invoice-' + i} idx={i + 1} invoice={x} linkHiddenStateFunc={(s: Function) => addInvoiceState(s)} />)}
                        </div>
                    )
                : '' }
        </div>
    );
}

// -------------------------------------
// INVOICE
// -------------------------------------
interface InvoiceProps {
    invoice: EFactRecord[];
    idx: number;
    linkHiddenStateFunc: Function;
}

function Invoice({ invoice, idx, linkHiddenStateFunc }: Readonly<InvoiceProps>): React.ReactElement {
    const [hidden, setHidden] = useState(true);
    linkHiddenStateFunc(setHidden);
    const type50s = invoice.filter((x) => x.type === 50) as RecordType50[];
    // Checks for invoice
    const error = [];

    // If total doesn't match sums of 50 types
    const total = (((invoice.find((x) => x.type === 80) as RecordType80).tekenEnTotaalBedragRekeningNrA * 1) / 100).toFixed(2);
    const summed = type50s.reduce((a, b) => a + (b.tekenEnBedragVerzekeringsTegemoetkoming * 1) / 100, 0).toFixed(2);
    if (total !== summed) {
        error.push('Het totaal bedrag in zone 80 is niet hetzelfde als de sommatie van alle record type 50s in deze factuur');
    }

    // Check if "tekenEnBedragSupplement" has same sign as "tekenEnBedragVerzekeringsTegemoetkoming"
    if (type50s.filter((x) => (x.tekenEnBedragSupplement * x.tekenEnBedragVerzekeringsTegemoetkoming) < 0).length) {
        error.push('Het teken van tekenEnBedragSupplement en tekenEnBedragVerzekeringsTegemoetkoming komt niet overeen. Mogelijk is dit door oude supplement instellingen die nu gezien worden als pseudo-nomenclaturen in plaats van als supplementen');
    }

    return (
        <div>
            <div className={Styles.invoice} style={{ color: error.length ? 'red' : 'block' }} onClick={() => setHidden(!hidden)}>Invoice {idx} - NRN: {(invoice[0] as RecordType20).identificatieRechthebbende}</div>
            { error.length ? error.map((e) => <div key={e} className={Styles.error} style={{ display: hidden ? 'none' : 'block' }}>{e}</div>) : '' }
            { invoice.map((x, i) => <Zone key={'zone-' + i} idx={i} zone={x} hidden={hidden} />) }
        </div>
    );
}

// -------------------------------------
// ZONE
// -------------------------------------
function Zone({ zone, hidden, idx }: Readonly<{ zone: any; hidden: boolean; idx: number }>): React.ReactElement {
    return (
        <div style={{ padding: '10px', display: hidden ? 'none' : 'block' }}>
            <table>
                <tbody>
                    { Object.keys(zone).map((x, i) => (
                        <tr key={'zone-prop-' + idx + '-' + i}>
                            <td>{x}</td>
                            <td>{zone[x]}</td>
                        </tr>
                    )) }
                </tbody>
            </table>
        </div>
    );
}

// -------------------------------------
// COMPARE RESULT
// -------------------------------------
interface CompareResultProps {
    open: boolean;
    onClose: MouseEventHandler<HTMLButtonElement>;
    diffs: ConfirmedNomenclature[];
    totalAmount: number;
}

function CompareResultDialog({ open, onClose, diffs, totalAmount }: Readonly<CompareResultProps>): React.ReactElement {
    return (
        <Dialog open={open}>
            <DialogTitle>Comparison Result</DialogTitle>
            <DialogContent>
                <div>
                    { !diffs.length
                        && (
                            <div>
                                <div>No difference, everything looking good!</div>
                                <br />
                                <div>NOTE: We are not checking Pseudo, EFactIgnored and Supplement-annotated nomenclatures from the ConfirmedNomenclatures table as they are not sent to the mutualities</div>
                            </div>
                        )}
                    { !!diffs.length
                        && (
                            <div>
                                <div>Found the following nomenclatures in the DB that are not found in the TFac file you chose: ({diffs.length} of {totalAmount})</div>
                                <div>NOTE: We are not checking Pseudo, EFactIgnored and Supplement-annotated nomenclatures from the ConfirmedNomenclatures table as they are not sent to the mutualities</div>
                                <br />
                                <table>
                                    <thead>
                                        <tr>
                                            <td>Invoice ID</td>
                                            <td>Code</td>
                                            <td>Nomenclature</td>
                                            <td>Parent Nomenclature</td>
                                            <td>Order_HasProcedure ID</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        { diffs.toSorted((a, b) => a.Invoice_UID - b.Invoice_UID).map((d, i) => (
                                            <tr key={`${d.EFactInvoiceNumber}_${d.NomenclatureNumber}`}>
                                                <td>{d.Invoice_UID}</td>
                                                <td>{d.Code}</td>
                                                <td>{d.NomenclatureNumber}</td>
                                                <td>{d.ParentNomenclatureNumber}</td>
                                                <td>{d.OrderHasProcedure_UID}</td>
                                            </tr>
                                        ),
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                </div>
            </DialogContent>
            <DialogButtons>
                <Button onClick={onClose}>Close</Button>
            </DialogButtons>
        </Dialog>
    );
}

function ServerError({ err, onClose }: Readonly<{ err: string; onClose: MouseEventHandler<HTMLButtonElement> }>): React.ReactElement {
    return (
        <Dialog open={true}>
            <DialogTitle>Server Error/Issue</DialogTitle>
            <DialogContent>
                <div style={{ whiteSpace: 'pre-line' }}>{err}</div>
            </DialogContent>
            <DialogButtons>
                <Button onClick={onClose}>Close</Button>
            </DialogButtons>
        </Dialog>
    );
}

export default EFactTool;
